import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { IconBadge } from '../components/icons/ServiceIcons';
import './ServicePage.css';
const cloudArtwork = '/assets/illustrations/cloud-architecture.svg';

const Cloud = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [processRef, processInView] = useInView({ threshold: 0.2, triggerOnce: true });

  const features = [
    {
      icon: 'cloudUpload',
      title: 'Cloud Migration',
      description: 'Seamless migration of applications and data with battle-tested runbooks that minimise downtime and retire technical debt.'
    },
    {
      icon: 'scaling',
      title: 'Auto-Scaling Solutions',
      description: 'Elastic architectures that respond to demand in real time, balancing performance, resilience, and financial governance.'
    },
    {
      icon: 'shield',
      title: 'Security & Compliance',
      description: 'Enterprise security patterns covering identity, encryption, and compliance mandates including SOC 2, HIPAA, and GDPR.'
    },
    {
      icon: 'savings',
      title: 'Cost Optimisation',
      description: 'Reserved capacity, FinOps guardrails, and consumption dashboards that consistently reduce cloud spend by 30-60%.'
    },
    {
      icon: 'recovery',
      title: 'Disaster Recovery',
      description: 'Automated backup, failover, and resiliency drills to guarantee recovery objectives and continuous business operations.'
    },
    {
      icon: 'multiCloud',
      title: 'Multi-Cloud Strategy',
      description: 'Vendor-neutral landing zones and governance frameworks that leverage the strengths of each cloud while avoiding lock-in.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Cloud Readiness Assessment',
      description: 'Comprehensive analysis of infrastructure, applications, and business drivers to determine the optimal cloud target state.',
      icon: 'assessment'
    },
    {
      step: '02',
      title: 'Architecture Design',
      description: 'Design scalable, secure, and cost-effective architectures with reference guardrails for networking, identity, and observability.',
      icon: 'architecture'
    },
    {
      step: '03',
      title: 'Migration Planning',
      description: 'Sequence migration waves with risk mitigation, interdependency mapping, and detailed runbooks to protect business continuity.',
      icon: 'roadmap'
    },
    {
      step: '04',
      title: 'Implementation & Migration',
      description: 'Execute migrations with automated guardrails, validation checkpoints, and transparent status reporting for stakeholders.',
      icon: 'rocket'
    },
    {
      step: '05',
      title: 'Optimization & Support',
      description: 'Provide 24/7 managed services, FinOps insights, and iterative optimisation to keep platforms at peak performance.',
      icon: 'analytics'
    }
  ];

  const cloudPlatforms = [
    {
      name: 'Amazon Web Services',
      tagline: 'AWS',
      description: 'Landing zones, Control Tower, and workload modernisation across IaaS, PaaS, and data services.',
      icon: 'cloud'
    },
    {
      name: 'Microsoft Azure',
      tagline: 'Azure',
      description: 'Enterprise-scale reference architectures, Azure Arc, and hybrid operations with Defender integration.',
      icon: 'cloud'
    },
    {
      name: 'Google Cloud Platform',
      tagline: 'GCP',
      description: 'Anthos, GKE, BigQuery, and Vertex AI for data-intensive and multi-cloud solutions.',
      icon: 'cloud'
    },
    {
      name: 'Oracle & IBM Cloud',
      tagline: 'Specialty Clouds',
      description: 'Regulated workloads, ERP lift-and-shift, and mainframe modernisation with enterprise SLAs.',
      icon: 'cloud'
    },
    {
      name: 'DigitalOcean & Linode',
      tagline: 'Developer Clouds',
      description: 'Cost-conscious hosting, managed databases, and edge nodes for scale-up startups.',
      icon: 'cloud'
    },
    {
      name: 'Kubernetes & Anthos',
      tagline: 'Orchestration',
      description: 'Platform engineering, GitOps, and policy-as-code for multi-cluster operations.',
      icon: 'multiCloud'
    },
    {
      name: 'Serverless & EventBridge',
      tagline: 'Event-driven',
      description: 'Lambda, Functions, and event buses powering resilient microservices and async workflows.',
      icon: 'spark'
    },
    {
      name: 'CloudFormation & Bicep',
      tagline: 'Infrastructure as Code',
      description: 'Native templates alongside Terraform for standardised environment provisioning.',
      icon: 'infrastructure'
    },
    {
      name: 'HashiCorp Terraform & Packer',
      tagline: 'IaC Tooling',
      description: 'Reusable modules, immutable images, and multi-cloud automation pipelines.',
      icon: 'toolkit'
    },
    {
      name: 'Cloudflare & Akamai',
      tagline: 'Edge & Security',
      description: 'Global CDN, WAF, zero-trust access, and performance acceleration at the edge.',
      icon: 'shield'
    },
    {
      name: 'Datadog & New Relic',
      tagline: 'Observability',
      description: 'Unified metrics, logs, traces, and cost analytics for hybrid cloud operations.',
      icon: 'monitor'
    },
    {
      name: 'CloudHealth & Azure Cost Management',
      tagline: 'FinOps',
      description: 'Budget guardrails, optimisation insights, and chargeback for multi-cloud portfolios.',
      icon: 'savings'
    }
  ];

  const marqueeRows = [
    cloudPlatforms.filter((_, index) => index % 2 === 0),
    cloudPlatforms.filter((_, index) => index % 2 === 1)
  ].filter((row) => row.length);

  const benefits = [
    {
      title: 'Scalability',
      description: 'Scale resources up or down instantly based on demand without compromising performance.',
      icon: 'scaling'
    },
    {
      title: 'Cost Efficiency',
      description: 'Optimise spend with usage-based pricing, tagging discipline, and centralised FinOps guardrails.',
      icon: 'savings'
    },
    {
      title: 'Global Reach',
      description: 'Deploy applications worldwide with low latency and compliance-aligned regional footprints.',
      icon: 'multiCloud'
    },
    {
      title: 'Innovation Velocity',
      description: 'Leverage cutting-edge managed services, AI platforms, and automation to accelerate feature delivery.',
      icon: 'spark'
    }
  ];

  return (
    <motion.div 
      className="service-page cloud-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Hero Section */}
      <section className="service-hero parallax-container" ref={heroRef}>
        <div className="parallax-bg"></div>
        <div className="container">
          <motion.div 
            className="hero-content text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div 
              className="hero-icon"
              initial={{ scale: 0 }}
              animate={heroInView ? { scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <img 
                src={cloudArtwork} 
                alt="Abstract cloud architecture illustration" 
              />
            </motion.div>
            <h1>Cloud Services</h1>
            <p className="hero-description">
              Transform mission-critical platforms with engineered landing zones, orchestrated migrations, and managed cloud operations. We pair strategy and execution so every environment remains secure, optimised, and ready for rapid innovation.
            </p>
            <Link to="/contact" className="btn">Start Your Cloud Journey</Link>
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="what-we-do section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>What We Do</h2>
            <p>
              We provide end-to-end cloud services that help businesses modernize their 
              infrastructure, reduce operational costs, and accelerate innovation. Our 
              cloud experts ensure secure, scalable, and efficient cloud adoption tailored 
              to your specific business needs and compliance requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section section section-alt" ref={featuresRef}>
        <div className="container">
          <div className="grid-3">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="feature-card card"
                initial={{ opacity: 0, y: 50 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="feature-icon">
                  <IconBadge name={feature.icon} size={72} backgroundOpacity={0.24} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section section" ref={processRef}>
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={processInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Cloud Adoption Process</h2>
            <p>A structured methodology for successful cloud transformation</p>
          </motion.div>
          
          <div className="process-timeline">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                className="process-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={processInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="process-number">{item.step}</div>
                <div className="process-content">
                  <div className="process-icon">
                    <IconBadge
                      name={item.icon}
                      className="icon-badge--sm"
                      size={58}
                      backgroundOpacity={0.24}
                    />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="technologies-section section section-alt">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Cloud Platforms & Technologies</h2>
            <p>We work with leading cloud providers and cutting-edge technologies</p>
          </motion.div>
          
          <motion.div
            className="tech-marquee-wrapper"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {marqueeRows.map((row, rowIndex) => (
              <div
                className={`tech-marquee${rowIndex % 2 === 1 ? ' tech-marquee--reverse' : ''}`}
                key={`cloud-row-${rowIndex}`}
              >
                <div className="tech-marquee__track">
                  {[...row, ...row].map((platform, index) => (
                    <div className="tech-marquee__card" key={`cloud-tech-${rowIndex}-${index}-${platform.name}`}>
                      <IconBadge
                        name={platform.icon}
                        className="tech-marquee__badge icon-badge--sm"
                        size={58}
                        backgroundOpacity={0.24}
                      />
                      <div className="tech-marquee__label">
                        <span className="tech-marquee__title">{platform.name}</span>
                        {platform.tagline && (
                          <span className="tech-marquee__subtitle">{platform.tagline}</span>
                        )}
                        {platform.description && (
                          <span className="tech-marquee__description">{platform.description}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Why Choose Cloud?</h2>
          </motion.div>
          
          <div className="grid-4">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={benefit.title}
                className="benefit-item"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                viewport={{ once: true }}
              >
                <div className="benefit-icon">
                  <IconBadge
                    name={benefit.icon}
                    className="icon-badge--sm"
                    size={56}
                    backgroundOpacity={0.24}
                  />
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Cloud;
